import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useChecklistServicoItensCadastroPagination } from "../hooks/ChecklistServicoItens_cadastro/pagination";
import { ItemChecklistServico } from "../../domain/models/item_checklist_servico";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ChecklistServicoDetalhe } from "../../domain/models/checklist_servico_detalhe";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { ItemChecklistServicoForm } from "../schemas/ChecklistServicoItens_cadastro/item_checklist_servico";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { ChecklistServicoAmbienteModel } from "../../domain/models/checklist_servico_ambiente";

/**
 * LoadItensChecklistServicoFuncState
 */

type LoadItensChecklistServicoFuncState = {
  idChecklistServicoAmbiente?: number;

  page?: number;
  rowsPerPage?: number;
};

/**
 * useChecklistServicoItensCadastroViewModel
 */

export const useChecklistServicoItensCadastroViewModel = () => {
  const {
    obras: {
      getChecklistServicoByIdUseCase,
      getChecklistServicoAmbienteByIdUseCase,
      getAllItemChecklistServicoUseCase,
      createItemChecklistServicoUseCase,
      updateItemChecklistServicoUseCase,
      deleteItemChecklistServicoUseCase,
      deleteSelectedItemChecklistServicoUseCase: deleteSelectedChecklistServicoUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [selectedChecklistServicoAmbiente, setSelectedChecklistServicoAmbiente] = useState<ChecklistServicoAmbienteModel | undefined>();
  const [checklistServico, setChecklistServico] = useState<ChecklistServicoDetalhe>();

  const [selectedItemChecklistServico, setSelectedItemChecklistServico] = useState<ItemChecklistServico>();

  const [itensChecklistServico, setItensChecklistServico] = useState<ItemChecklistServico[]>();

  const [itemChecklistServicoModal, setItemChecklistServicoModal] = useState(false);
  const [itemChecklistServicoDeleteModal, setItemChecklistServicoDeleteModal] = useState(false);
  const [selectedChecklistServicoDeleteModal, setSelectedChecklistServicoDeleteModal] = useState(false);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();
  const pagination = useChecklistServicoItensCadastroPagination();

  /**
   * Funcoes de carregamento
   */
  const loadChecklistServicoEAmbiente = useCallback(async (idAmbiente: number) => {
    try {
      const ambienteResult = await getChecklistServicoAmbienteByIdUseCase.execute(
        idAmbiente,
      );

      if (ambienteResult) {
        const checklistResult = await getChecklistServicoByIdUseCase.getChecklistServicoById(
          ambienteResult.idChecklistServico,
        );
        setChecklistServico(checklistResult);
        setSelectedChecklistServicoAmbiente(ambienteResult);
      } else {
        setChecklistServico(undefined);
        setSelectedChecklistServicoAmbiente(undefined);
      }
    } catch (e: unknown) {
      setChecklistServico(undefined);
      throw new Error('Falha ao buscar checklist de serviço!');
    }
  }, [
    getChecklistServicoAmbienteByIdUseCase,
    getChecklistServicoByIdUseCase,
  ]);

  const loadItensChecklistServico = useCallback(async (fs?: LoadItensChecklistServicoFuncState) => {
    try {
      const filteredIdChecklistServicoAmbiente = fs?.idChecklistServicoAmbiente ?? selectedChecklistServicoAmbiente?.idChecklistServicoAmbiente;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllItemChecklistServicoUseCase.getAllItemChecklistServico(
        filteredIdChecklistServicoAmbiente,
        filteredPage,
        filteredRowsPerPage,
      );

      setItensChecklistServico(getAllItemChecklistServicoUseCase.items);
      pagination.setTotalItems(getAllItemChecklistServicoUseCase.totalItems);
    } catch (e: unknown) {
      setItensChecklistServico(undefined);
      throw new Error('Falha ao buscar itens do checklist de serviço!');
    }
  }, [
    getAllItemChecklistServicoUseCase,
    pagination,
    selectedChecklistServicoAmbiente
  ]);

  /**
   * useEffect
   */
  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationIdChecklistServico = params.get('id');
      const locIdChecklistServicoAmbiente = locationIdChecklistServico ?? undefined;

      if (!locIdChecklistServicoAmbiente || locIdChecklistServicoAmbiente.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de serviço!");
        return;
      }

      await loadChecklistServicoEAmbiente(Number(locIdChecklistServicoAmbiente));
      await loadItensChecklistServico({ idChecklistServicoAmbiente: Number(locIdChecklistServicoAmbiente) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/ChecklistServico_lista' });
  }, [
    backOrNavigate
  ]);

  const handleReload = useCallback(async () => {
    if (selectedChecklistServicoAmbiente) {
      try {
        setLoading(true);

        await loadChecklistServicoEAmbiente(selectedChecklistServicoAmbiente.idChecklistServicoAmbiente);
        await loadItensChecklistServico({ idChecklistServicoAmbiente: selectedChecklistServicoAmbiente.idChecklistServicoAmbiente });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedChecklistServicoAmbiente,
    loadChecklistServicoEAmbiente,
    loadItensChecklistServico,
    pushErrorNotification,
  ]);

  const handleItemChecklistServicoSubmit = useCallback(async (form: ItemChecklistServicoForm) => {
    if (selectedChecklistServicoAmbiente) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedItemChecklistServico) {
          await updateItemChecklistServicoUseCase.updateItemChecklistServico(
            selectedItemChecklistServico.idItemChecklistServico,
            form.ordem,
            form.descricao,
            form.resumoItem,
            form.metodoVerificacao,
            form.tolerancia,
          );
        } else {
          await createItemChecklistServicoUseCase.createItemChecklistServico(
            selectedChecklistServicoAmbiente.idChecklistServicoAmbiente,
            form.ordem,
            form.descricao,
            form.resumoItem,
            form.metodoVerificacao,
            form.tolerancia,
          );
        }
        await loadItensChecklistServico();
        setItemChecklistServicoModal(false);
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao salvar item de checklist de serviço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedChecklistServicoAmbiente,
    selectedItemChecklistServico,
    createItemChecklistServicoUseCase,
    updateItemChecklistServicoUseCase,
    loadItensChecklistServico,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleItemChecklistServicoDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedItemChecklistServico) {
        await deleteItemChecklistServicoUseCase.deleteItemChecklistServico(
          selectedItemChecklistServico.idItemChecklistServico,
        );
      }

      await loadItensChecklistServico();
      setItemChecklistServicoDeleteModal(false);

      pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao excluir item de checklist de serviço!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedItemChecklistServico,
    deleteItemChecklistServicoUseCase,
    loadItensChecklistServico,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleSelectedChecklistServicoDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
        await deleteSelectedChecklistServicoUseCase.deleteSelectedItemChecklistServico(
          Array.from(rowSelectionModel.ids).map(x => typeof x === 'number' ? x : Number(x)),
        );
      }

      await loadItensChecklistServico();
      setSelectedChecklistServicoDeleteModal(false);

      pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      setSelectedChecklistServicoDeleteModal(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao excluir item de checklist de serviço!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    rowSelectionModel,
    deleteSelectedChecklistServicoUseCase,
    loadItensChecklistServico,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useChecklistServicoItensCadastroPagination.setState({ page: page + 1 });
      await loadItensChecklistServico({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadItensChecklistServico,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useChecklistServicoItensCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadItensChecklistServico({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadItensChecklistServico,
    pushErrorNotification,
  ]);

  const handleItemChecklistServicoModalOpenState = useCallback((idItemChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idItemChecklistServico && itensChecklistServico) {
        setSelectedItemChecklistServico(itensChecklistServico.find(x => x.idItemChecklistServico === idItemChecklistServico));
      } else {
        setSelectedItemChecklistServico(undefined);
      }
    }
    setItemChecklistServicoModal(openState);
  }, [
    itensChecklistServico,
  ]);

  const handleItemChecklistServicoDeleteModalOpenState = useCallback((idItemChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idItemChecklistServico && itensChecklistServico) {
        setSelectedItemChecklistServico(itensChecklistServico.find(x => x.idItemChecklistServico === idItemChecklistServico));
      } else {
        setSelectedItemChecklistServico(undefined);
      }
    }
    setItemChecklistServicoDeleteModal(openState);
  }, [
    itensChecklistServico,
  ]);

  const handleSelectedChecklistServicoDeleteModalOpenState = useCallback((openState: boolean) => {
    if (openState) {
      if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
        setSelectedChecklistServicoDeleteModal(openState);
      } else {
        pushWarnNotification('Atenção', 'Você deve selecionar pelo menos um item para exclusão!');
      }
    } else {
      setSelectedChecklistServicoDeleteModal(openState);
    }
  }, [
    rowSelectionModel,
    pushWarnNotification,
  ]);

  const handleRowSelectionModelChange = useCallback((newRowSelectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
  }, [
  ]);

  return {
    loading,
    saving,
    errorMessage,
    checklistServico,
    selectedItemChecklistServico: selectedItemChecklistServico ?? null,
    itensChecklistServico: itensChecklistServico ?? [],
    itemChecklistServicoModal,
    itemChecklistServicoDeleteModal,
    selectedChecklistServicoDeleteModal,
    rowSelectionModel,
    pagination,
    selectedChecklistServicoAmbiente,

    handleBack,
    handleReload,
    handleItemChecklistServicoSubmit,
    handleItemChecklistServicoDeleteSubmit,
    handleSelectedChecklistServicoDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleItemChecklistServicoModalOpenState,
    handleItemChecklistServicoDeleteModalOpenState,
    handleSelectedChecklistServicoDeleteModalOpenState,
    handleRowSelectionModelChange,
  };
}
